import { Component } from '@theme/component';

/**
 * Wishlist compartida entre todos los botones de corazon y la pagina de
 * favoritos. Se guarda en localStorage porque Horizon usa las New Customer
 * Accounts de Shopify (sin templates de cuenta locales ni backend propio
 * todavia), asi que no hay ningun sitio server-side donde persistir esto por
 * cliente. Cualquier producto guardado es local a este navegador.
 */

const STORAGE_KEY = 'trendaze:wishlist';
const CHANGE_EVENT = 'wishlist:change';

/**
 * @typedef {{ id: string, handle: string }} WishlistItem
 */

/** @returns {WishlistItem[]} */
function readWishlist() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {WishlistItem[]} items */
function writeWishlist(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage puede no estar disponible (modo privado, cuota llena...).
    // El boton sigue respondiendo visualmente en esta pestana, solo que no
    // persiste entre sesiones.
  }

  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
}

/** @param {string} productId */
export function isWishlisted(productId) {
  return readWishlist().some((item) => item.id === productId);
}

/** @returns {WishlistItem[]} */
export function getWishlistItems() {
  return readWishlist();
}

/**
 * @param {WishlistItem} product
 * @returns {boolean} true si se ha anadido, false si se ha quitado
 */
export function toggleWishlistItem(product) {
  const items = readWishlist();
  const index = items.findIndex((item) => item.id === product.id);
  let added;

  if (index === -1) {
    items.push(product);
    added = true;
  } else {
    items.splice(index, 1);
    added = false;
  }

  writeWishlist(items);
  return added;
}

/** @param {string} productId */
export function removeWishlistItem(productId) {
  writeWishlist(readWishlist().filter((item) => item.id !== productId));
}

/**
 * Boton de corazon reutilizable (tarjeta de producto, ficha de producto...).
 * Lee/escribe la wishlist compartida y se mantiene sincronizado con el resto
 * de botones de la misma pagina via el evento `wishlist:change`.
 */
export class WishlistButtonComponent extends Component {
  requiredRefs = ['control'];

  connectedCallback() {
    super.connectedCallback();
    this.productId = this.dataset.productId ?? '';
    this.productHandle = this.dataset.productHandle ?? '';
    this.boundSync = this.sync.bind(this);
    document.addEventListener(CHANGE_EVENT, this.boundSync);
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.boundSync);
  }

  sync() {
    const active = isWishlisted(this.productId);
    this.classList.toggle('is-active', active);
    this.refs.control?.setAttribute('aria-pressed', String(active));
  }

  toggle() {
    if (!this.productId || !this.productHandle) return;
    toggleWishlistItem({ id: this.productId, handle: this.productHandle });
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButtonComponent);
}
